import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ExternalLink, Bot, Smartphone, Mic, Bell, BarChart3, Globe2, CreditCard,
  CheckCircle, ArrowRight
} from 'lucide-react';

const Showcase = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = ['All', 'Salons & Spas', 'Medical Clinics', 'Fitness & Wellness', 'Consultants'];

  const examples = [
    {
      title: 'Glamour Studio',
      category: 'Salons & Spas',
      tag: 'Salon',
      features: ['Online Booking', 'WhatsApp Integration', 'Gallery', 'Reviews'],
      image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=400&fit=crop'
    },
    {
      title: 'HealthFirst Clinic',
      category: 'Medical Clinics',
      tag: 'Clinic',
      features: ['Appointment System', 'Doctor Profiles', 'Patient Portal', 'Lab Reports'],
      image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&h=400&fit=crop'
    },
    {
      title: 'Serenity Spa & Wellness',
      category: 'Salons & Spas',
      tag: 'Spa',
      features: ['Service Menu', 'Package Deals', 'Gift Cards', 'Staff Profiles'],
      image: 'https://images.unsplash.com/photo-1540555700478-4be289fbec6e?w=600&h=400&fit=crop'
    },
    {
      title: 'FitZone Studio',
      category: 'Fitness & Wellness',
      tag: 'Fitness',
      features: ['Class Schedule', 'Membership Plans', 'Trainer Profiles', 'Progress Tracking'],
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop'
    },
    {
      title: 'CA Mehta & Associates',
      category: 'Consultants',
      tag: 'Consultant',
      features: ['Online Consultation', 'Document Upload', 'Fee Calculator', 'Client Portal'],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop'
    }
  ];

  const futuristicFeatures = [
    {
      icon: Bot, title: 'AI-Powered Chatbot',
      items: ['Answers customer queries 24/7', 'Books appointments automatically', 'Speaks Hindi & English', 'WhatsApp integration'],
      gradient: 'from-primary-500 to-primary-700'
    },
    {
      icon: Smartphone, title: 'Progressive Web App',
      items: ['Install on home screen', 'Offline booking', 'Push notifications', 'Native app feel'],
      gradient: 'from-secondary-500 to-secondary-700'
    },
    {
      icon: Mic, title: 'Voice Booking',
      items: ['Book appointment via voice', 'Multilingual support', 'Accessibility-first', 'Natural language'],
      gradient: 'from-accent-500 to-accent-700'
    },
    {
      icon: Bell, title: 'Smart Notifications',
      items: ['WhatsApp reminders', 'Email confirmations', 'SMS alerts', 'Rescheduling links'],
      gradient: 'from-primary-600 to-secondary-500'
    },
    {
      icon: BarChart3, title: 'Analytics Dashboard',
      items: ['Real-time booking stats', 'Customer insights', 'Revenue tracking', 'Growth recommendations'],
      gradient: 'from-accent-600 to-primary-500'
    },
    {
      icon: Globe2, title: 'Multi-location Support',
      items: ['Manage multiple branches', 'Centralized dashboard', 'Location-specific pricing', 'Unified reporting'],
      gradient: 'from-secondary-600 to-primary-500'
    },
    {
      icon: CreditCard, title: 'Payment Integration',
      items: ['Razorpay / Paytm', 'UPI payments', 'Booking deposits', 'Refund automation'],
      gradient: 'from-primary-500 to-accent-500'
    }
  ];

  const filtered = activeFilter === 'All' ? examples : examples.filter(e => e.category === activeFilter);

  return (
    <>
      {/* Live Examples */}
      <section id="examples" className="py-20 lg:py-28 bg-white" data-testid="live-examples-section">
        <div className="container">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-secondary-50 text-secondary-600 font-medium rounded-full text-sm mb-4">
              Portfolio
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              See What We Build
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="example-filters">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                  activeFilter === f
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {filtered.map((example, i) => (
              <motion.div
                key={example.title}
                className="card-base group overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`example-card-${i}`}
              >
                <div className="relative h-52 -mx-8 -mt-8 mb-6 overflow-hidden">
                  <img
                    src={example.image}
                    alt={example.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-lg">
                    {example.tag}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-neutral-900 mb-3">
                  {example.title}
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {example.features.map((f) => (
                    <span key={f} className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-lg">
                      {f}
                    </span>
                  ))}
                </div>
                <button className="inline-flex items-center gap-2 text-primary-500 font-medium text-sm hover:gap-3 transition-all" data-testid={`view-site-${i}`}>
                  View Live Site <ExternalLink className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Futuristic Features */}
      <section className="py-20 lg:py-28 bg-neutral-900 noise-overlay" data-testid="futuristic-features-section">
        <div className="container relative z-10">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-primary-500/20 text-primary-300 font-medium rounded-full text-sm mb-4">
              Future Ready
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Built for 2026. <span className="text-gradient-primary">Not 2016.</span>
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              Every website we build comes with cutting-edge features that keep you ahead of the competition.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {futuristicFeatures.map((feature, i) => (
              <motion.div
                key={feature.title}
                className="bg-neutral-800/50 backdrop-blur-sm border border-neutral-700/50 rounded-2xl p-6 hover:border-primary-500/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`future-feature-${i}`}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-3">
                  {feature.title}
                </h3>
                <ul className="space-y-2">
                  {feature.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-neutral-400 text-sm">
                      <CheckCircle className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Showcase;
